'use client';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import './index.scss';

export interface EfVideoProps {
    /**
     * 所有分辨率展示的视频链接
     */
    pc: string;
    /**
     * 分辨率[1024, 768]
     */
    pad?: string;
    /**
     * 分辨率[1024, 0) 或 (768, 0)
     */
    h5?: string;
    /**
     * 视频贴图-所有分辨率
     */
    poster?: string;
    /**
     * 视频贴图-所有分辨率
     */
    posterPad?: string;
    /**
     * 视频贴图-分辨率小于等于1024px
     */
    posterH5?: string;
    /**
     * 是否自动播放
     */
    autoPlay?: boolean;
    /**
     * 是否循环播放
     */
    loop?: boolean;
    /**
     * 自定义重播按钮svg icon
     */
    Icon?: any;
    /**
     * 样式名
     */
    className?: string;
    /**
     * 开始播放
     */
    startPlay?: boolean;
    /**
     * 是否预加载
     */
    preload?: 'auto' | 'metadata' | 'none';
    fetchpriority?: 'high' | 'low' | 'auto';
}

/**
 * 用于播放视频
 *
 * 视频右上角会有个播放按钮,在不播放时展示
 */
export default function EfVideo({
    className = '',
    autoPlay,
    loop = false,
    poster,
    posterPad,
    posterH5,
    pc,
    pad,
    h5,
    Icon,
    startPlay,
    preload = 'auto',
    fetchpriority = 'auto',
}: EfVideoProps) {
    const prefixCls = 'ef-video';
    const compCls = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-has-pad`]: pad,
    });
    const videoRefPc = useRef(null);
    const videoRefPad = useRef(null);
    const videoRefH5 = useRef(null);
    const [showPlayIcon, setShowPlayIcon] = useState(!autoPlay);
    const [isPlay, setIsPlay] = useState(false);

    // 获取选中的元素
    const getShowVideo = () => {
        const videoA: any = videoRefPc?.current;
        const videoB: any = videoRefPad?.current;
        const videoC: any = videoRefH5?.current;
        const blockA = videoA && window.getComputedStyle(videoA).display;
        const blockB = videoB && window.getComputedStyle(videoB).display;
        const blockC = videoC && window.getComputedStyle(videoC).display;
        if (blockA === 'block') {
            return videoRefPc;
        }
        if (blockB === 'block') {
            return videoRefPad;
        }
        if (blockC === 'block') {
            return videoRefH5;
        }
        return null;
    };
    // 点击播放
    const playHandle = () => {
        setIsPlay(true);
        const videoCur: any = getShowVideo()?.current;

        if (videoCur) {
            videoCur.play();
            setIsPlay(true);
            setShowPlayIcon(false);
        }
    };
    useEffect(() => {
        const videoA: any = getShowVideo()?.current;

        if (videoA) {
            videoA.addEventListener('ended', () => {
                setIsPlay(false);
                setShowPlayIcon(true);
            });
        }
    }, []);

    useEffect(() => {
        if (startPlay) {
            playHandle();
        }
    }, [startPlay]);

    return (
        <div className={compCls}>
            {h5 && (
                <video
                    ref={videoRefH5}
                    playsInline
                    muted
                    autoPlay={autoPlay}
                    loop={loop}
                    poster={posterH5 || poster}
                    preload={preload}
                    className="ef-video-item ef-video-item-h5"
                    aria-label="EF Video"
                    // eslint-disable-next-line react/no-unknown-property
                    webkit-playsinline="true"
                    // eslint-disable-next-line react/no-unknown-property
                    x5-playsinline="true"
                    // eslint-disable-next-line react/no-unknown-property
                    x5-video-player-type="h5-page"
                >
                    <source src={h5} />
                </video>
            )}
            {pad && (
                <video
                    ref={videoRefPad}
                    playsInline
                    muted
                    autoPlay={autoPlay}
                    loop={loop}
                    poster={posterPad || poster}
                    preload={preload}
                    fetchpriority={fetchpriority}
                    className="ef-video-item ef-video-item-pad"
                    aria-label="EF Video"
                    // eslint-disable-next-line react/no-unknown-property
                    webkit-playsinline="true"
                    // eslint-disable-next-line react/no-unknown-property
                    x5-playsinline="true"
                    // eslint-disable-next-line react/no-unknown-property
                    x5-video-player-type="h5-page"
                >
                    <source src={pad} />
                </video>
            )}

            {pc && (
                <video
                    ref={videoRefPc}
                    playsInline
                    muted
                    autoPlay={autoPlay}
                    loop={loop}
                    poster={poster}
                    preload={preload}
                    fetchpriority={fetchpriority}
                    className="ef-video-item ef-video-item-pc"
                    aria-label="EF Video"
                    // eslint-disable-next-line react/no-unknown-property
                    webkit-playsinline="true"
                    // eslint-disable-next-line react/no-unknown-property
                    x5-playsinline="true"
                    // eslint-disable-next-line react/no-unknown-property
                    x5-video-player-type="h5-page"
                >
                    <source src={pc} />
                </video>
            )}
            {showPlayIcon &&
                !isPlay &&
                (Icon ? (
                    <Icon className={`${prefixCls}-icon`} onClick={playHandle} />
                ) : (
                    <svg
                        className={`${prefixCls}-icon`}
                        onClick={playHandle}
                        xmlns="http://www.w3.org/2000/svg"
                        width="100"
                        height="100"
                        viewBox="0 0 100 100"
                        fill="none"
                    >
                        <path
                            d="M94.1667 49.8611C94.1667 74.3304 74.3304 94.1667 49.8611 94.1667C25.3918 94.1667 5.55557 74.3304 5.55557 49.8611C5.55557 25.3918 25.3918 5.55557 49.8611 5.55557C74.3304 5.55557 94.1667 25.3918 94.1667 49.8611Z"
                            stroke="white"
                            strokeWidth="2.22222"
                        />
                        <path
                            d="M37.7783 67.9227V34.0886C37.7783 32.3267 39.7308 31.2655 41.2093 32.2239L66.1396 48.3835C67.4577 49.2378 67.4975 51.1526 66.2162 52.0611L41.2858 69.7356C39.8142 70.7789 37.7783 69.7266 37.7783 67.9227Z"
                            fill="white"
                        />
                    </svg>
                ))}
        </div>
    );
}
